// ============================================================================
// 18급 ~ 9단 맞춤형 정밀 장기 AI & 전술 교환(Quiescence) + 다중 깊이(Multi-Ply) 검증 엔진
// - 자충수(말리는 추천수) 원천 차단을 위한 Quiescence Search(장군 중 Stand-Pat 금지 + 포획/장군 연쇄 탐색)
// - searchDepth 파라미터가 실제 alphaBeta + quiescenceSearch 탐색 깊이를 구동
// - 훈수(訓手) 자격 판정(Eligibility) 및 희생타/미끼수(Sacrifice/Decoy) 심층 비교 지원
// ============================================================================

import {
  BoardGrid,
  Move,
  Piece,
  PieceType,
  Pos,
  Side,
  applyMoveOnClone,
  getAllLegalMoves,
  getPseudoLegalMovesForPiece,
  isKingInCheck
} from './JanggiRules.js';
import { getTierByIndex } from './TierSystem.js';

// Centipawn scale: 1 point of Janggi material = 100 cp
export const CP_VALUES: Record<PieceType, number> = {
  king: 20000,
  cha: 1300,
  po: 700,
  ma: 500,
  sang: 320,
  sa: 300,
  jol: 200
};

// Positional bonus tables (from Cho's perspective: row 9 is home, row 0 is enemy palace)
const JOL_POS_BONUS = [
  [10, 20, 35, 50, 60, 50, 35, 20, 10],
  [20, 45, 70, 95, 110, 95, 70, 45, 20],
  [25, 50, 80, 105, 120, 105, 80, 50, 25],
  [20, 40, 60, 85, 95, 85, 60, 40, 20],
  [15, 25, 40, 55, 65, 55, 40, 25, 15],
  [10, 18, 25, 35, 45, 35, 25, 18, 10],
  [0, 5, 10, 15, 25, 15, 10, 5, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0]
];

// Find all enemy pieces that can pseudo-legally attack `targetPos` on `board`
export function getAttackersOfSquare(
  board: BoardGrid,
  targetPos: Pos,
  attackerSide: Side
): Array<{ from: Pos; piece: Piece }> {
  const attackers: Array<{ from: Pos; piece: Piece }> = [];
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      if (!p || p.side !== attackerSide) continue;
      const targets = getPseudoLegalMovesForPiece(board, { row: r, col: c });
      if (targets.some(t => t.row === targetPos.row && t.col === targetPos.col)) {
        attackers.push({ from: { row: r, col: c }, piece: p });
      }
    }
  }
  return attackers;
}

// Check if a friendly square `pos` is defended by another friendly piece if captured
export function isSquareDefendedBySide(
  board: BoardGrid,
  targetPos: Pos,
  defenderSide: Side,
  attackerFrom?: Pos
): boolean {
  const simBoard = board.map(row => row.map(cell => (cell ? { ...cell } : null)));
  const enemySide: Side = defenderSide === 'cho' ? 'han' : 'cho';
  const dummyType: PieceType = attackerFrom && simBoard[attackerFrom.row]?.[attackerFrom.col]
    ? simBoard[attackerFrom.row][attackerFrom.col]!.type
    : 'jol';
  if (attackerFrom) {
    simBoard[attackerFrom.row][attackerFrom.col] = null;
  }
  simBoard[targetPos.row][targetPos.col] = { id: 'dummy_enemy', side: enemySide, type: dummyType };

  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      if (r === targetPos.row && c === targetPos.col) continue;
      const p = simBoard[r][c];
      if (!p || p.side !== defenderSide) continue;
      const targets = getPseudoLegalMovesForPiece(simBoard, { row: r, col: c });
      if (targets.some(t => t.row === targetPos.row && t.col === targetPos.col)) {
        return true;
      }
    }
  }
  return false;
}

// Evaluate board in centipawns (cp) from `perspective` side's viewpoint
export function evaluateBoardCp(
  board: BoardGrid,
  perspective: Side = 'cho'
): number {
  let choScore = 0;
  let hanScore = 150; // 한(후수) 덤 1.5점 = 150cp

  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 9; c++) {
      const p = board[r][c];
      if (!p) continue;

      const base = CP_VALUES[p.type];
      let posBonus = 0;
      const relRow = p.side === 'cho' ? r : 9 - r;

      if (p.type === 'jol') {
        posBonus = JOL_POS_BONUS[relRow][c];
        if (c > 0 && board[r][c - 1]?.side === p.side && board[r][c - 1]?.type === 'jol') posBonus += 18;
      } else if (p.type === 'ma' || p.type === 'sang') {
        const centerDist = Math.abs(4 - c) + Math.abs(4.5 - r);
        posBonus = Math.round((9 - centerDist) * 16);
        if (relRow === 9) posBonus -= 40;
        else if (relRow === 7 && (c === 2 || c === 6)) posBonus += 38;
      } else if (p.type === 'po') {
        if (c === 4) posBonus += 50;
        if (relRow === 7 && c === 4) posBonus += 55;
      } else if (p.type === 'cha') {
        if (relRow <= 6) posBonus += 42;
        if (relRow <= 3) posBonus += 35;
        if (c === 3 || c === 4 || c === 5) posBonus += 28;
      } else if (p.type === 'sa' || p.type === 'king') {
        if (c === 4 && (r === 1 || r === 8)) posBonus += 35;
      }

      if (p.side === 'cho') choScore += base + posBonus;
      else hanScore += base + posBonus;
    }
  }

  const rawChoCp = choScore - hanScore;
  return perspective === 'cho' ? rawChoCp : -rawChoCp;
}

export function cpToWinRatePercent(cp: number): number {
  if (cp >= 9000) return 99.5;
  if (cp <= -9000) return 0.5;
  const k = 0.00155;
  const prob = 1 / (1 + Math.exp(-k * cp));
  const rawPct = Math.round(prob * 1000) / 10;
  return Math.max(3.5, Math.min(96.5, rawPct));
}

export interface RankedMoveEvaluation {
  move: Move;
  scoreCp: number;          // Verified post-response score from mover's perspective
  shallowScoreCp?: number;  // Depth-2 baseline score for stability comparison
  deepScoreCp?: number;     // Depth-3+ verified score
  scoreAfterChoCp: number;  // Verified post-response score from Cho's perspective
  bestOpponentReply?: Move; // Predicted best opponent response (2nd ply)
  bestFollowUpMove?: Move;  // Recommended next move for the player after opponent replies (3rd ply)
  isSelfTrap: boolean;      // True if this move hangs a piece or loses material without tactical recovery
}

export function scoreMoveForOrdering(m: Move): number {
  let s = 0;
  if (m.captured) {
    s += CP_VALUES[m.captured.type] * 12 - CP_VALUES[m.piece.type];
  }
  if (m.gaveCheck) s += 550;
  if (m.wasMeonggun) s += 750;

  const relFromRow = m.piece.side === 'cho' ? m.from.row : 9 - m.from.row;
  const relToRow = m.piece.side === 'cho' ? m.to.row : 9 - m.to.row;

  if (m.piece.type === 'ma' || m.piece.type === 'sang') {
    if (relFromRow === 9 && relToRow < 9) s += 165;
  } else if (m.piece.type === 'po') {
    if (m.to.col === 4 && relToRow === 7) s += 190;
    else if (m.to.col === 4) s += 120;
  } else if (m.piece.type === 'cha') {
    if (relToRow < relFromRow) s += 140;
  } else if (m.piece.type === 'jol') {
    if (m.from.col === 4 || m.to.col === 4) s += 85;
    else s += 35;
  }
  return s;
}

// ============================================================================
// Quiescence Search:
// - 장군(Check) 상태일 때는 절대 standPat을 허용하지 않고 합법 응수 전체를 탐색!
// - 포획 및 결정적 장군 연쇄를 끝까지 추적해 미끼수·희생타 외통수를 정확히 판별
// ============================================================================
export function quiescenceSearch(
  board: BoardGrid,
  alpha: number,
  beta: number,
  sideToMove: Side,
  maximizingSide: Side,
  qDepth = 3
): number {
  const inCheck = isKingInCheck(board, sideToMove);
  const isMax = sideToMove === maximizingSide;

  if (!inCheck) {
    const standPat = evaluateBoardCp(board, maximizingSide);
    if (qDepth <= 0) {
      return standPat;
    }
    if (isMax) {
      if (standPat >= beta) return beta;
      if (standPat > alpha) alpha = standPat;
    } else {
      if (standPat <= alpha) return alpha;
      if (standPat < beta) beta = standPat;
    }
  } else if (qDepth < -2) {
    // Safety cap against perpetual check loops in quiescence
    return evaluateBoardCp(board, maximizingSide);
  }

  const allMoves = getAllLegalMoves(board, sideToMove);
  if (allMoves.length === 0) {
    if (inCheck) {
      return isMax ? -18500 - qDepth * 100 : 18500 + qDepth * 100;
    }
    return evaluateBoardCp(board, maximizingSide);
  }

  const tacticalMoves = inCheck
    ? allMoves
    : allMoves.filter(
        m => (m.captured && CP_VALUES[m.captured.type] >= 200) || (qDepth >= 2 && m.gaveCheck)
      );

  if (tacticalMoves.length === 0) {
    return evaluateBoardCp(board, maximizingSide);
  }

  tacticalMoves.sort((a, b) => scoreMoveForOrdering(b) - scoreMoveForOrdering(a));
  const nextSide: Side = sideToMove === 'cho' ? 'han' : 'cho';
  const limit = inCheck ? tacticalMoves.length : Math.min(tacticalMoves.length, 8);

  if (isMax) {
    let best = inCheck ? -Infinity : evaluateBoardCp(board, maximizingSide);
    for (let i = 0; i < limit; i++) {
      const m = tacticalMoves[i];
      const { nextBoard } = applyMoveOnClone(board, m.from, m.to);
      const score = quiescenceSearch(nextBoard, alpha, beta, nextSide, maximizingSide, qDepth - 1);
      if (score > best) best = score;
      if (best > alpha) alpha = best;
      if (alpha >= beta) break;
    }
    return best;
  } else {
    let best = inCheck ? Infinity : evaluateBoardCp(board, maximizingSide);
    for (let i = 0; i < limit; i++) {
      const m = tacticalMoves[i];
      const { nextBoard } = applyMoveOnClone(board, m.from, m.to);
      const score = quiescenceSearch(nextBoard, alpha, beta, nextSide, maximizingSide, qDepth - 1);
      if (score < best) best = score;
      if (best < beta) beta = best;
      if (alpha >= beta) break;
    }
    return best;
  }
}

export function alphaBeta(
  board: BoardGrid,
  depth: number,
  alpha: number,
  beta: number,
  sideToMove: Side,
  maximizingSide: Side
): number {
  if (depth <= 0) {
    return quiescenceSearch(board, alpha, beta, sideToMove, maximizingSide, 3);
  }

  const legalMoves = getAllLegalMoves(board, sideToMove);
  if (legalMoves.length === 0) {
    const inCheck = isKingInCheck(board, sideToMove);
    if (inCheck) {
      return sideToMove === maximizingSide ? -18500 - depth * 100 : 18500 + depth * 100;
    }
    return 0;
  }

  legalMoves.sort((a, b) => scoreMoveForOrdering(b) - scoreMoveForOrdering(a));
  const candidateMoves = depth >= 3 ? legalMoves.slice(0, 14) : legalMoves.slice(0, 12);
  const nextSide: Side = sideToMove === 'cho' ? 'han' : 'cho';

  if (sideToMove === maximizingSide) {
    let value = -Infinity;
    for (const m of candidateMoves) {
      const { nextBoard } = applyMoveOnClone(board, m.from, m.to);
      const childScore = alphaBeta(nextBoard, depth - 1, alpha, beta, nextSide, maximizingSide);
      if (childScore > value) value = childScore;
      if (value > alpha) alpha = value;
      if (alpha >= beta) break;
    }
    return value;
  } else {
    let value = Infinity;
    for (const m of candidateMoves) {
      const { nextBoard } = applyMoveOnClone(board, m.from, m.to);
      const childScore = alphaBeta(nextBoard, depth - 1, alpha, beta, nextSide, maximizingSide);
      if (childScore < value) value = childScore;
      if (value < beta) beta = value;
      if (alpha >= beta) break;
    }
    return value;
  }
}

// ============================================================================
// Evaluate a specific board state AFTER a move was just made by `moverSide`
// ============================================================================
export function evaluatePositionAfterMoveCp(
  boardAfterMove: BoardGrid,
  moverSide: Side,
  depth = 1
): number {
  const enemySide: Side = moverSide === 'cho' ? 'han' : 'cho';
  if (depth >= 2) {
    return alphaBeta(boardAfterMove, depth - 1, -Infinity, Infinity, enemySide, moverSide);
  }
  return quiescenceSearch(boardAfterMove, -Infinity, Infinity, enemySide, moverSide, 3);
}

// ============================================================================
// Analyze Top Moves with Real `searchDepth` Support & 3-Ply PV Extraction
// - searchDepth = 2: 2-ply + quiescence search
// - searchDepth >= 3: refines top candidates with alphaBeta(depth = searchDepth - 1)
// ============================================================================
export function analyzeTopMoves(
  board: BoardGrid,
  side: Side,
  searchDepth = 2,
  topN = 3
): RankedMoveEvaluation[] {
  const legalMoves = getAllLegalMoves(board, side);
  if (legalMoves.length === 0) return [];

  legalMoves.sort((a, b) => scoreMoveForOrdering(b) - scoreMoveForOrdering(a));
  const candidates = legalMoves.slice(0, Math.min(legalMoves.length, 36));
  const enemySide: Side = side === 'cho' ? 'han' : 'cho';
  const baselineCp = evaluateBoardCp(board, side);

  const evaluated: RankedMoveEvaluation[] = [];

  for (const m of candidates) {
    const { nextBoard: boardAfter1 } = applyMoveOnClone(board, m.from, m.to);

    let selfTrapPenalty = 0;
    if (m.piece.type !== 'jol' && m.piece.type !== 'king') {
      const enemyAttackers = getAttackersOfSquare(boardAfter1, m.to, enemySide);
      if (enemyAttackers.length > 0) {
        const cheapestEnemy = enemyAttackers.sort((a, b) => CP_VALUES[a.piece.type] - CP_VALUES[b.piece.type])[0];
        const myPieceVal = CP_VALUES[m.piece.type];
        const gainVal = m.captured ? CP_VALUES[m.captured.type] : 0;
        const isDefended = isSquareDefendedBySide(boardAfter1, m.to, side, cheapestEnemy.from);

        if (!isDefended && myPieceVal > gainVal) {
          selfTrapPenalty = (myPieceVal - gainVal) * 0.95;
        } else if (isDefended && myPieceVal - CP_VALUES[cheapestEnemy.piece.type] > gainVal + 120) {
          selfTrapPenalty = (myPieceVal - CP_VALUES[cheapestEnemy.piece.type] - gainVal) * 0.9;
        }
      }
    }

    const oppMoves = getAllLegalMoves(boardAfter1, enemySide);
    let bestOpponentReply: Move | undefined;
    let bestFollowUpMove: Move | undefined;
    let worstScoreForUs = Infinity;

    if (oppMoves.length === 0) {
      worstScoreForUs = isKingInCheck(boardAfter1, enemySide) ? 19000 : 0;
    } else {
      oppMoves.sort((a, b) => scoreMoveForOrdering(b) - scoreMoveForOrdering(a));
      const oppCandidates = oppMoves.slice(0, Math.min(oppMoves.length, 18));

      for (const oppM of oppCandidates) {
        const { nextBoard: boardAfter2 } = applyMoveOnClone(boardAfter1, oppM.from, oppM.to);
        const childScore =
          oppM.captured || oppM.gaveCheck || m.gaveCheck
            ? quiescenceSearch(boardAfter2, -Infinity, Infinity, side, side, 2)
            : evaluateBoardCp(boardAfter2, side);

        if (childScore < worstScoreForUs) {
          worstScoreForUs = childScore;
          bestOpponentReply = oppM;
        }
      }

      if (bestOpponentReply) {
        const { nextBoard: boardAfterBestOpp } = applyMoveOnClone(
          boardAfter1,
          bestOpponentReply.from,
          bestOpponentReply.to
        );
        const followUps = getAllLegalMoves(boardAfterBestOpp, side);
        if (followUps.length > 0) {
          followUps.sort((a, b) => scoreMoveForOrdering(b) - scoreMoveForOrdering(a));
          let bestFollowScore = -Infinity;
          for (const fm of followUps.slice(0, 16)) {
            const { nextBoard: boardAfter3 } = applyMoveOnClone(boardAfterBestOpp, fm.from, fm.to);
            const fScore =
              fm.captured || fm.gaveCheck
                ? quiescenceSearch(boardAfter3, -Infinity, Infinity, enemySide, side, 2)
                : evaluateBoardCp(boardAfter3, side);
            const synergyBonus =
              (fm.from.row === m.to.row && fm.from.col === m.to.col ? 45 : 0) +
              (fm.captured ? 65 : 0) +
              (fm.gaveCheck ? 55 : 0);
            if (fScore + synergyBonus > bestFollowScore) {
              bestFollowScore = fScore + synergyBonus;
              bestFollowUpMove = fm;
            }
          }
        }
      }
    }

    // If the move is a sacrifice checkmate or deep tactical win (worstScoreForUs >= baselineCp),
    // do not apply the naive static selfTrapPenalty!
    const effectivePenalty = worstScoreForUs >= baselineCp + 150 ? 0 : selfTrapPenalty;
    const shallowScoreCp = worstScoreForUs - effectivePenalty;
    const isSelfTrap = effectivePenalty >= 220 || worstScoreForUs < baselineCp - 260;
    const scoreAfterChoCp = side === 'cho' ? shallowScoreCp : -shallowScoreCp;

    evaluated.push({
      move: m,
      scoreCp: shallowScoreCp,
      shallowScoreCp,
      deepScoreCp: shallowScoreCp,
      scoreAfterChoCp,
      bestOpponentReply,
      bestFollowUpMove,
      isSelfTrap
    });
  }

  evaluated.sort((a, b) => b.scoreCp - a.scoreCp);

  // When searchDepth >= 3, refine the top candidates using genuine multi-ply alphaBeta
  if (searchDepth >= 3 && evaluated.length > 0) {
    const refineCount = Math.min(evaluated.length, Math.max(topN * 3, 8));
    for (let i = 0; i < refineCount; i++) {
      const item = evaluated[i];
      const { nextBoard: boardAfter1 } = applyMoveOnClone(board, item.move.from, item.move.to);
      const deepCp = alphaBeta(
        boardAfter1,
        Math.min(4, searchDepth - 1),
        -Infinity,
        Infinity,
        enemySide,
        side
      );
      item.deepScoreCp = deepCp;
      item.scoreCp = Math.round(deepCp * 0.7 + (item.shallowScoreCp ?? deepCp) * 0.3);
      item.scoreAfterChoCp = side === 'cho' ? item.scoreCp : -item.scoreCp;
      if (deepCp >= baselineCp - 50) {
        item.isSelfTrap = false;
      } else if (deepCp < baselineCp - 260) {
        item.isSelfTrap = true;
      }
    }
    evaluated.sort((a, b) => b.scoreCp - a.scoreCp);
  }

  return evaluated.slice(0, topN);
}

export function selectAiMoveForTier(board: BoardGrid, side: Side, tierIndex: number): Move | null {
  const tier = getTierByIndex(tierIndex);
  const topEvaluations = analyzeTopMoves(board, side, Math.max(2, Math.min(3, tier.aiDepth)), 5);
  if (topEvaluations.length === 0) return null;

  const safeEvaluations = topEvaluations.filter(e => !e.isSelfTrap);
  const pool = safeEvaluations.length > 0 ? safeEvaluations : topEvaluations;

  if (Math.random() > tier.aiRandomness || pool.length === 1) {
    return pool[0].move;
  }

  const poolSize = Math.min(pool.length, tier.index <= 8 ? 3 : 2);
  const pickIdx = Math.floor(Math.random() * poolSize);
  return pool[pickIdx].move;
}

export function isDestinationSelfTrap(board: BoardGrid, from: Pos, to: Pos): boolean {
  const piece = board[from.row]?.[from.col];
  if (!piece || piece.type === 'jol' || piece.type === 'king') return false;
  const enemySide: Side = piece.side === 'cho' ? 'han' : 'cho';
  const { nextBoard, captured } = applyMoveOnClone(board, from, to);

  // If this move delivers checkmate, it is never a self-trap!
  const oppMoves = getAllLegalMoves(nextBoard, enemySide);
  if (oppMoves.length === 0 && isKingInCheck(nextBoard, enemySide)) {
    return false;
  }

  const attackers = getAttackersOfSquare(nextBoard, to, enemySide);
  if (attackers.length === 0) return false;

  const cheapestAttacker = attackers.sort((a, b) => CP_VALUES[a.piece.type] - CP_VALUES[b.piece.type])[0];
  const { nextBoard: afterEnemyCap } = applyMoveOnClone(nextBoard, cheapestAttacker.from, to);
  if (isKingInCheck(afterEnemyCap, enemySide)) return false;

  const myVal = CP_VALUES[piece.type];
  const capturedVal = captured ? CP_VALUES[captured.type] : 0;
  const defended = isSquareDefendedBySide(nextBoard, to, piece.side, cheapestAttacker.from);

  if (!defended && myVal > capturedVal) return true;
  if (defended && myVal - CP_VALUES[cheapestAttacker.piece.type] > capturedVal + 100) return true;
  return false;
}
